import { format } from 'date-fns'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExpandMore } from '@mui/icons-material'
import { Box, Button, Card, Collapse, IconButton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { AnimatedImage } from '../../../../components/AnimatedImage/AnimatedImage'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { HtmlContentParser } from '../../../../components/HtmlContentParser/HtmlContentParser'
import { ImageCarouselModal } from '../../../../components/ImageCarouselModal/ImageCarouselModal'
import { ModalMode, liveEventReviewModalParserOptions } from '../../../../components/LiveEventModalLink/LiveEventModalLink'
import { borderRadiusDefault, borderRadiusInset } from '../../../../themes/theme'
import { useInternalAccessCheck } from '../../../account/hooks/roleHooks'
import { LiveEventEvolutionEntry, LiveEventEvolutionTypeEnum } from '../../types/LiveEventCollection'
import { TrackingEvent } from '../../types/TrackingEvents'
import { LIVE_EVENT_EVOLUTION_TYPE_COLOR } from '../LiveEventEvolutionTypeChip/LiveEventEvolutionTypeChip'
import { LiveEventEvolutionTypeTag } from '../LiveEventEvolutionTypeTag/LiveEventEvolutionTypeTag'
import {
  BlueprintVersionTagSlots,
  formatEventDateRange,
  getEventDurationDays,
  hasMechanicChange,
  hasReskin,
  isNewEntry,
  isValidTimestamp,
  LiveEventEvolutionScope,
} from '../helpers/liveEventEvolutionTimelineHelpers'
import {
  ROW_BOTTOM_SPACING,
  ROW_NODE_OFFSET,
  SPINE_CENTER,
  SPINE_COLOR,
  SPINE_COLUMN_GAP,
  SPINE_COLUMN_WIDTH,
  SPINE_DOT_RING,
  SPINE_DOT_SIZE,
  SPINE_LINE_LEFT,
  SPINE_STUB_WIDTH,
} from '../helpers/liveEventEvolutionTimelineLayout'

const SCREENSHOT_WIDTH = 110
/** Duration reads as a column rather than as text pushed around by the label's length. */
const DURATION_COLUMN_WIDTH = 52

interface LiveEventEvolutionTimelineRowProps {
  entry: LiveEventEvolutionEntry
  /** Resolved tracking event for the entry — absent while the events query is still filling in. */
  event?: TrackingEvent
  gameId: string
  /** Entry name with the blueprint prefix shared by the whole list already removed. */
  displayName: string
  /** Tag-group color of the entry's event type, used for the spine dot. */
  dotColor: string
  isCurrent: boolean
  isExpanded: boolean
  instanceCount: number
  /** Tag columns reserved across the whole list so the rows align with each other. */
  tagSlots: BlueprintVersionTagSlots
  /** The scope the timeline is listing; LATEST keeps the entry-level mechanic change flag. */
  scope: LiveEventEvolutionScope
  /** First paragraph of the analyst review comment. */
  descriptionRawHtml?: string | null
  /** What changed in this specific version. */
  versionDescriptionHtml?: string
  onToggle: () => void
  onOpenVersion: () => void
  onOpenInstancesTab?: () => void
}

export const LiveEventEvolutionTimelineRow: React.FC<LiveEventEvolutionTimelineRowProps> = ({
  entry,
  event,
  gameId,
  displayName,
  dotColor,
  isCurrent,
  isExpanded,
  instanceCount,
  tagSlots,
  scope,
  descriptionRawHtml,
  versionDescriptionHtml,
  onToggle,
  onOpenVersion,
  onOpenInstancesTab,
}) => {
  const { t } = useTranslation()
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const mechanicChange = hasMechanicChange(entry, scope)
  const reskin = hasReskin(entry)
  const isNew = isNewEntry(entry, event)
  const mechanicChangeColor = LIVE_EVENT_EVOLUTION_TYPE_COLOR[LiveEventEvolutionTypeEnum.MechanicChange] as string
  const inactive = event?.active === false
  const isInternalUser = useInternalAccessCheck()
  const showWipMessage = inactive && !isInternalUser
  const durationDays = getEventDurationDays(event?.start, event?.end)
  const dateRange = formatEventDateRange(event?.start, event?.end)
  const screenshotUrl = event?.screenshotUrl
  const hasVersionDescription = Boolean(versionDescriptionHtml && versionDescriptionHtml.trim().length > 0)

  const parserOptions = liveEventReviewModalParserOptions(gameId, { liveEventModalMode: ModalMode.Replace })

  const stopPropagation = (handler: () => void) => (mouseEvent: React.MouseEvent) => {
    mouseEvent.stopPropagation()
    handler()
  }

  const labelSx = {
    display: 'block',
    fontSize: '10.5px',
    fontWeight: 'bold',
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    mb: 0.5,
  } as const

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${SPINE_COLUMN_WIDTH}px 1fr`,
        columnGap: SPINE_COLUMN_GAP,
        alignItems: 'stretch',
      }}
    >
      {/* Spine cell: the continuing vertical line, the branch stub and the entry dot. */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', left: `${SPINE_LINE_LEFT}px`, top: 0, bottom: 0, width: '2px', backgroundColor: SPINE_COLOR }} />
        <Box
          sx={{
            position: 'absolute',
            left: `${SPINE_CENTER}px`,
            top: `${ROW_NODE_OFFSET}px`,
            width: `${SPINE_STUB_WIDTH}px`,
            height: '2px',
            backgroundColor: SPINE_COLOR,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: `${SPINE_CENTER + SPINE_STUB_WIDTH}px`,
            top: `${ROW_NODE_OFFSET + 1}px`,
            transform: 'translate(-50%, -50%)',
            width: `${SPINE_DOT_SIZE}px`,
            height: `${SPINE_DOT_SIZE}px`,
            borderRadius: '50%',
            backgroundColor: dotColor,
            boxShadow: (theme) => `0 0 0 ${SPINE_DOT_RING}px ${isCurrent ? theme.palette.primary.main : theme.palette.common.white}`,
          }}
        />
      </Box>

      <Card
        className="LiveEventEvolutionTimelineCard"
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
        elevation={0}
        sx={{
          // The row gap lives on the card, not on the grid: the spine column
          // stretches over it so the line stays unbroken between rows.
          mb: ROW_BOTTOM_SPACING,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: isCurrent ? 'primary.main' : 'divider',
          // The version being viewed is drawn a shade heavier, so "you are here"
          // survives being one card among thirty. The rest keep their hairline.
          ...(isCurrent && { borderWidth: '2px' }),
          // Inactive events keep their warning top bar from the previous design.
          ...(inactive && { borderTopWidth: '3px', borderTopColor: 'warning.main' }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px 10px 16px' }}>
          {isValidTimestamp(entry.start) && (
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 'bold', color: 'text.secondary', minWidth: '90px', flexShrink: 0 }}>
              {format(entry.start, 'MMM dd, yyyy')}
            </Typography>
          )}

          <Typography component="span" sx={{ fontSize: '14.5px', fontWeight: 'bold', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {displayName}
          </Typography>

          {isCurrent && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                padding: '2px 10px',
                borderRadius: borderRadiusDefault,
                backgroundColor: 'primary.main',
                color: 'common.white',
                fontSize: '10px',
                fontWeight: 'bold',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                lineHeight: 1.6,
                whiteSpace: 'nowrap',
              }}
            >
              {t('live-events:live_event_evolution_viewing')}
            </Box>
          )}

          {tagSlots.firstAppearance && <LiveEventEvolutionTypeTag type={LiveEventEvolutionTypeEnum.Highlighted} placeholder={!isNew} />}
          {tagSlots.firstSeen && <LiveEventEvolutionTypeTag type={LiveEventEvolutionTypeEnum.FirstInstanceSeen} placeholder={!entry.firstInstance} />}
          {tagSlots.mechanicChange && <LiveEventEvolutionTypeTag type={LiveEventEvolutionTypeEnum.MechanicChange} placeholder={!mechanicChange} />}
          {tagSlots.reskin && <LiveEventEvolutionTypeTag type={LiveEventEvolutionTypeEnum.Reskin} placeholder={!reskin} />}

          <Typography
            component="span"
            sx={{ fontSize: '12px', color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap', minWidth: `${DURATION_COLUMN_WIDTH}px`, textAlign: 'right' }}
          >
            {durationDays !== undefined ? t('live-events:day', { count: durationDays }) : ''}
          </Typography>

          <IconButton
            size="small"
            onClick={stopPropagation(onToggle)}
            sx={{
              flexShrink: 0,
              width: '24px',
              height: '24px',
              backgroundColor: 'background.default',
              '&:hover': { backgroundColor: 'background.default' },
            }}
          >
            <ExpandMore sx={{ fontSize: '16px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease-in-out' }} />
          </IconButton>
        </Box>

        <Collapse in={isExpanded} unmountOnExit>
          <Box
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              padding: '12px 16px 14px',
              display: 'grid',
              gridTemplateColumns: screenshotUrl ? `1fr ${SCREENSHOT_WIDTH}px` : '1fr',
              gap: '14px',
              textAlign: 'left',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {dateRange && (
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mb: 1.25 }}>
                  {t('live-events:live_event_evolution_range_instances', { range: dateRange, count: instanceCount })}
                </Typography>
              )}

              {hasVersionDescription &&
                (mechanicChange ? (
                  <Box
                    sx={{
                      backgroundColor: alpha(mechanicChangeColor, 0.08),
                      border: `1px solid ${alpha(mechanicChangeColor, 0.35)}`,
                      borderRadius: borderRadiusInset,
                      padding: '8px 12px',
                      mb: 1.5,
                    }}
                  >
                    <Typography component="span" sx={{ ...labelSx, color: mechanicChangeColor }}>
                      {t('live-events:live_event_evolution_mechanic_change_in_this_version')}
                    </Typography>
                    <Typography component="div" sx={{ fontSize: '13px', lineHeight: 1.5 }}>
                      <HtmlContentParser rawHtml={versionDescriptionHtml as string} parserOptions={parserOptions} />
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
                      {t('live-events:live_event_evolution_mechanic_change_scope_note')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography component="span" sx={labelSx}>
                      {t('live-events:change_in_this_version')}
                    </Typography>
                    <Typography component="div" sx={{ fontSize: '13px', lineHeight: 1.5 }}>
                      <HtmlContentParser rawHtml={versionDescriptionHtml as string} parserOptions={parserOptions} />
                    </Typography>
                  </Box>
                ))}

              {showWipMessage ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography component="span" sx={labelSx}>
                    {t('common:description')}
                  </Typography>
                  <Typography component="div" sx={{ fontSize: '13px', lineHeight: 1.5, fontStyle: 'italic' }}>
                    {t('live-events:event_wip_tooltip')}
                  </Typography>
                </Box>
              ) : (
                descriptionRawHtml && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography component="span" sx={labelSx}>
                      {t('common:description')}
                    </Typography>
                    <Typography component="div" sx={{ fontSize: '13px', lineHeight: 1.5 }}>
                      <HtmlContentParser rawHtml={descriptionRawHtml} parserOptions={parserOptions} />
                    </Typography>
                  </Box>
                )
              )}
            </Box>

            {screenshotUrl && (
              <Box sx={{ width: `${SCREENSHOT_WIDTH}px` }}>
                <GRTooltip content={t('live-events:event_dialog_screenshot_maximize')}>
                  <Box
                    onClick={stopPropagation(() => setLightboxOpen(true))}
                    sx={{
                      cursor: 'zoom-in',
                      // Hairline keeps light screenshots from bleeding into the card.
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: borderRadiusInset,
                      overflow: 'hidden',
                      lineHeight: 0,
                    }}
                  >
                    <AnimatedImage
                      url={screenshotUrl}
                      alt={displayName}
                      imgStyle={{ width: `${SCREENSHOT_WIDTH}px`, borderRadius: borderRadiusInset }}
                      autoPlay={false}
                      lazy={false}
                    />
                  </Box>
                </GRTooltip>
              </Box>
            )}
          </Box>

          {/* Actions sit in their own footer strip so they read as the card's, not the left column's. */}
          <Box
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              padding: '10px 16px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Button variant="contained" color="primary" size="small" onClick={stopPropagation(onOpenVersion)}>
              {t('live-events:live_event_evolution_open_this_version')}
            </Button>
            {onOpenInstancesTab && (
              <Button variant="outlined" color="primary" size="small" onClick={stopPropagation(onOpenInstancesTab)}>
                {t('live-events:live_event_evolution_see_instances')}
              </Button>
            )}
          </Box>
        </Collapse>
      </Card>

      {screenshotUrl && (
        <ImageCarouselModal
          open={lightboxOpen}
          imageUrls={[screenshotUrl]}
          onClose={() => setLightboxOpen(false)}
          title={displayName}
          crossOrigin="anonymous"
        />
      )}
    </Box>
  )
}

export default LiveEventEvolutionTimelineRow
